import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AlertTriangle, BarChart3, Key } from "lucide-react";
import { Card } from "@libs/rubick";
import { formatBytesPrecise, getCurrencySymbol } from "@/utils/formatters";
export const ProgressCard = ({ currentApiKeysUsed, totalApiKeysCount, totalConsumptionUsed, usageUnit, usagePlan, dateRange })=>{
    // Use totalApiKeysCount if provided (for calculating percentage against org limit)
    // Otherwise use currentApiKeysUsed (backward compatibility)
    const apiKeysForPercentage = totalApiKeysCount ?? currentApiKeysUsed;
    const apiKeysPercentage = apiKeysForPercentage / usagePlan.apiKeysLimit * 100;
    const apiKeysProgressWidth = Math.min(Math.max(apiKeysPercentage, 0), 100);
    const apiKeysRemaining = Math.max(usagePlan.apiKeysLimit - apiKeysForPercentage, 0);
    // Convert totalConsumptionUsed to bytes if it's not already
    // The API returns totalUsageValue in the unit specified by usageUnit
    const convertToBytes = (value, unit)=>{
        const unitLower = unit.toLowerCase();
        if (unitLower === "bytes" || unitLower === "byte") return value;
        if (unitLower === "kb") return value * 1024;
        if (unitLower === "mb") return value * 1024 * 1024;
        if (unitLower === "gb") return value * 1024 * 1024 * 1024;
        if (unitLower === "tb") return value * 1024 * 1024 * 1024 * 1024;
        return value; // Unknown unit, return as is
    };
    const consumptionInBytes = convertToBytes(totalConsumptionUsed, usageUnit);
    // Find current tier based on usage
    const getCurrentTier = ()=>{
        if (!usagePlan.tieredPricing || usagePlan.tieredPricing.length === 0) {
            return null;
        }
        // Determine the unit of the tier boundaries
        // If the tier range contains "MB", "GB", etc., the boundaries are likely in MB
        // If it contains just numbers with "bytes", they're in bytes
        const firstTierRange = usagePlan.tieredPricing[0]?.range || "";
        const tierBoundariesInMB = firstTierRange.includes("MB") || firstTierRange.includes("GB") || firstTierRange.includes("KB");
        // Convert consumption to the appropriate unit for comparison
        const consumptionForComparison = tierBoundariesInMB ? consumptionInBytes / (1024 * 1024 // Convert bytes to MB
        ) : consumptionInBytes; // Keep in bytes
        // Find which tier the current usage falls into
        for(let i = 0; i < usagePlan.tieredPricing.length; i++){
            const tier = usagePlan.tieredPricing[i];
            // Priority: use raw min_inclusive and max_exclusive values if available
            // These come directly from the contract.pricing_terms.tiers
            if (tier.min_inclusive !== undefined && tier.min_inclusive !== null) {
                const min = tier.min_inclusive;
                const max = tier.max_exclusive === null || tier.max_exclusive === undefined ? Infinity : tier.max_exclusive;
                // Check if current usage falls in this tier
                if (consumptionForComparison >= min && (max === Infinity || consumptionForComparison < max)) {
                    // Calculate percentages using the same unit
                    const usageInTier = consumptionForComparison - min;
                    const tierSize = max === Infinity ? 0 : max - min;
                    return {
                        ...tier,
                        min,
                        max,
                        currentUsage: consumptionInBytes,
                        percentageInTier: tierSize === 0 ? 0 : usageInTier / tierSize * 100,
                        percentageOfMax: max === Infinity ? 0 : consumptionForComparison / max * 100
                    };
                }
            }
        }
        // If no match found, return the last tier if usage exceeds all tiers
        // or the first tier if usage is below all tiers
        if (usagePlan.tieredPricing.length > 0) {
            const lastTier = usagePlan.tieredPricing[usagePlan.tieredPricing.length - 1];
            const lastMin = lastTier.min_inclusive || 0;
            const lastMax = lastTier.max_exclusive;
            // Check if we're beyond all tiers
            if (lastMax && consumptionForComparison >= lastMax) {
                return {
                    ...lastTier,
                    min: lastMin,
                    max: Infinity,
                    currentUsage: consumptionInBytes,
                    percentageInTier: 100,
                    percentageOfMax: 100
                };
            }
            // Otherwise return the first tier
            const firstTier = usagePlan.tieredPricing[0];
            const min = firstTier.min_inclusive || 0;
            const max = firstTier.max_exclusive === null || firstTier.max_exclusive === undefined ? Infinity : firstTier.max_exclusive;
            return {
                ...firstTier,
                min,
                max,
                currentUsage: consumptionInBytes,
                percentageInTier: max === Infinity ? 0 : consumptionForComparison / max * 100,
                percentageOfMax: max === Infinity ? 0 : consumptionForComparison / max * 100
            };
        }
        return null;
    };
    const currentTier = getCurrentTier();
    const showTierWarning = currentTier && currentTier.max !== Infinity && currentTier.percentageInTier >= 80;
    const getMarketplaceQuotaInfo = ()=>{
        if (!usagePlan.marketplaceQuota) return null;
        const { includedQuotaValue, includedQuotaUnit, consumptionUnit } = usagePlan.marketplaceQuota;
        let quotaInConsumptionUnit = includedQuotaValue;
        if (includedQuotaUnit.toLowerCase() === "gb" && consumptionUnit.toLowerCase() === "kb") {
            quotaInConsumptionUnit = includedQuotaValue * 1024 * 1024; // GB to KB
        } else if (includedQuotaUnit.toLowerCase() === "mb" && consumptionUnit.toLowerCase() === "kb") {
            quotaInConsumptionUnit = includedQuotaValue * 1024; // MB to KB
        } else if (includedQuotaUnit.toLowerCase() === "gb" && consumptionUnit.toLowerCase() === "mb") {
            quotaInConsumptionUnit = includedQuotaValue * 1024; // GB to MB
        }
        const warningThreshold = quotaInConsumptionUnit * 0.8;
        const percentageUsed = consumptionInBytes / quotaInConsumptionUnit * 100;
        return {
            quotaInConsumptionUnit,
            warningThreshold,
            percentageUsed,
            showWarning: consumptionInBytes >= warningThreshold,
            includedQuotaValue,
            includedQuotaUnit
        };
    };
    const marketplaceQuotaInfo = getMarketplaceQuotaInfo();
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            marketplaceQuotaInfo?.showWarning && /*#__PURE__*/ _jsx("div", {
                className: "bg-yellow-50 border border-yellow-200 rounded-lg p-4",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center",
                    children: [
                        /*#__PURE__*/ _jsx(AlertTriangle, {
                            className: "h-5 w-5 text-yellow-600 mr-2"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm font-medium text-yellow-800",
                                    children: "Approaching Usage Limit"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm text-yellow-700 mt-1",
                                    children: [
                                        "You are at",
                                        " ",
                                        Math.round(Math.min(marketplaceQuotaInfo.percentageUsed, 100)),
                                        "% of your included ",
                                        marketplaceQuotaInfo.includedQuotaValue,
                                        " ",
                                        marketplaceQuotaInfo.includedQuotaUnit,
                                        " quota. Please contact Qrypt to increase your usage limit."
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            !usagePlan.marketplaceQuota && showTierWarning && /*#__PURE__*/ _jsx("div", {
                className: "bg-yellow-50 border border-yellow-200 rounded-lg p-4",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center",
                    children: [
                        /*#__PURE__*/ _jsx(AlertTriangle, {
                            className: "h-5 w-5 text-yellow-600 mr-2"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm font-medium text-yellow-800",
                                    children: "Approaching Tier Limit"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm text-yellow-700 mt-1",
                                    children: [
                                        "You are at",
                                        " ",
                                        Math.round(Math.min(currentTier.percentageInTier, 100)),
                                        "% of your current tier limit. You will move to the next pricing tier after exceeding ",
                                        currentTier.range,
                                        "."
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 md:grid-cols-2 gap-6",
                children: [
                    /*#__PURE__*/ _jsxs(Card, {
                        variant: "bordered",
                        title: "API Keys Usage",
                        icon: /*#__PURE__*/ _jsx(Key, {
                            className: "h-5 w-5 text-navy-70"
                        }),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mb-4 flex items-start justify-between",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-2xl font-bold text-gray-900",
                                                children: apiKeysForPercentage
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "mt-1 h-5",
                                                children: totalApiKeysCount !== undefined && totalApiKeysCount !== currentApiKeysUsed && /*#__PURE__*/ _jsxs("p", {
                                                    className: "text-xs text-gray-500",
                                                    children: [
                                                        "(",
                                                        currentApiKeysUsed,
                                                        " yours)"
                                                    ]
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "mt-1 text-sm text-gray-500",
                                        children: [
                                            "of ",
                                            usagePlan.apiKeysLimit,
                                            " allowed"
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mb-2 h-4 w-full rounded-full bg-gray-200",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "h-4 rounded-full bg-navy-70 transition-all duration-300",
                                    style: {
                                        width: `${apiKeysProgressWidth}%`
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-between text-sm text-gray-600",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            apiKeysRemaining,
                                            " remaining"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            Math.round(apiKeysPercentage),
                                            "% used"
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Card, {
                        variant: "bordered",
                        title: "Consumption Usage",
                        icon: /*#__PURE__*/ _jsx(BarChart3, {
                            className: "h-5 w-5 text-green-600"
                        }),
                        header: dateRange ? /*#__PURE__*/ _jsxs("p", {
                            className: "text-xs text-gray-500",
                            children: [
                                new Date(dateRange.startDate).toLocaleDateString(),
                                " -",
                                " ",
                                new Date(dateRange.endDate).toLocaleDateString()
                            ]
                        }) : null,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mb-4 flex items-center justify-between",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-2xl font-bold text-gray-900",
                                                children: formatBytesPrecise(consumptionInBytes)
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "mt-1 text-sm text-gray-500",
                                                children: usagePlan.marketplaceQuota ? `of ${marketplaceQuotaInfo?.includedQuotaValue} ${marketplaceQuotaInfo?.includedQuotaUnit} included` : currentTier ? `Currently in ${currentTier.tier}` : "No tier data available"
                                            })
                                        ]
                                    }),
                                    usagePlan.marketplaceQuota ? /*#__PURE__*/ _jsxs("div", {
                                        className: "text-right",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs text-gray-600",
                                                children: "AWS Marketplace"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "mt-1 text-xs text-gray-500",
                                                children: "Pricing managed by AWS"
                                            })
                                        ]
                                    }) : currentTier && currentTier.price !== undefined ? /*#__PURE__*/ _jsxs("div", {
                                        className: "text-right",
                                        children: [
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "text-sm font-medium text-gray-700",
                                                children: [
                                                    getCurrencySymbol(usagePlan.currency || "USD"),
                                                    currentTier.price,
                                                    "/mo"
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "mt-1 text-xs text-gray-500",
                                                children: currentTier.range
                                            })
                                        ]
                                    }) : null
                                ]
                            }),
                            usagePlan.marketplaceQuota && marketplaceQuotaInfo ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mb-2 h-4 w-full rounded-full bg-gray-200",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: `h-4 rounded-full transition-all duration-300 ${marketplaceQuotaInfo.percentageUsed >= 95 ? "bg-red-600" : marketplaceQuotaInfo.percentageUsed >= 80 ? "bg-yellow-600" : "bg-green-600"}`,
                                            style: {
                                                width: `${Math.min(marketplaceQuotaInfo.percentageUsed, 100)}%`
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex justify-between text-sm text-gray-600",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "Progress of included quota"
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    Math.round(Math.min(marketplaceQuotaInfo.percentageUsed, 100)),
                                                    "% used"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }) : currentTier && currentTier.max !== Infinity ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mb-2 h-4 w-full rounded-full bg-gray-200",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: `h-4 rounded-full transition-all duration-300 ${currentTier.percentageInTier >= 95 ? "bg-red-600" : currentTier.percentageInTier >= 80 ? "bg-yellow-600" : "bg-green-600"}`,
                                            style: {
                                                width: `${Math.min(currentTier.percentageInTier, 100)}%`
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex justify-between text-sm text-gray-600",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "Progress within tier"
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    Math.round(Math.min(currentTier.percentageInTier, 100)),
                                                    "% used"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }) : null
                        ]
                    })
                ]
            })
        ]
    });
};
